import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { useParams, useSearchParams } from 'react-router-dom'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { Customer, Payout as IPayout, ManagedBalanceLedger } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { StatusBadge } from './CheckoutSessions'
import _, { camelCase } from 'lodash'
import { errorToast, shortAddress, roundAssetAmount, successToast, trimZeros, unifyLedger, useTestMode } from '../util/Util'
import TimeText from '../components/TimeText'
import Table from '../components/Table'
import { IconButton } from '../components/Button'
import Copy from '../icons/Copy'
import { ExternalLinkText } from './Events'
import { formatExplorerUrl } from '../util/Chains'
import LinkExternal from '../components/LinkExternal'
import { getMethod, ManagedPaymentMethod, prodNetworks, testNetworks } from '../util/Managed'
import { Section, SectionContent, SectionItem, SubTitle } from '../components/Shared'

const Payout = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [payout, setPayout] = useState<IPayout>()
  const [isLoading, setIsLoading] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger[]>()
  const [testMode] = useTestMode()

  const fetchPayoutData = async (id: string): Promise<void> => {
    setIsLoading(true)
    let payout: IPayout
    try {
      payout = await Radom.getPayout(id)
      setPayout(payout)
      if (payout.payouts[0].payoutType.crypto) {
        setPaymentMethod(getMethod(
          payout.payouts[0].payoutType.crypto.network,
          payout.payouts[0].payoutType.crypto.token
        ))
      }
    } catch {
      setIsLoading(false)
      errorToast('Failed to load payout')
      return
    }

    try {
      const ledger = await Radom.listManagedBalanceLedger([payout.id])
      if (ledger.length === 1) {
        setManagedBalanceLedger(ledger)
      } else {
        const unified = unifyLedger(ledger)
        setManagedBalanceLedger(unified)
      }
    } catch {
      errorToast('Failed to load ledger')
    }

    const ids = Array.from(
      new Set(
        payout.payouts
          .map(it => it.payoutType.crypto?.customerId)
          .filter((id): id is string => Boolean(id))
      )
    )
    const needed = new Set(ids)
    const found: Customer[] = []
    const limit = 20
    const parallelQueries = 3
    const networks = testMode ? testNetworks : prodNetworks
    const sortType = 'createdAtDescending'

    try {
      const firtsPage = await Radom.listCustomers(0, limit, networks, sortType)
      const total = firtsPage.total
      if (total < limit) {
        for (const c of firtsPage.data) {
          if (needed.has(c.id)) {
            found.push(c)
            needed.delete(c.id)
            if (needed.size === 0) break
          }
        }
      } else {
        for (let baseOffset = limit; baseOffset < total && needed.size > 0; baseOffset += limit * parallelQueries) {
          const offsets: number[] = []
          for (let k = 0; k < parallelQueries; k++) {
            const off = baseOffset + k * limit
            if (off < total) offsets.push(off)
          }
          const pages = await Promise.all(
            offsets.map(async (off) => await Radom.listCustomers(off, limit, networks, sortType))
          )

          for (const page of pages) {
            for (const c of page.data) {
              if (needed.has(c.id)) {
                found.push(c)
                needed.delete(c.id)
                if (needed.size === 0) break
              }
            }
          }
        }
      }

      setCustomers(found)
    } catch {
      errorToast('Failed to load customers')
    }

    setIsLoading(false)
  }

  const debouncedFetchPayoutlData = useMemo(() => _.debounce(fetchPayoutData, 300), [])

  useEffect(() => {
    if (!params.id) return
    debouncedFetchPayoutlData(params.id)
    return () => debouncedFetchPayoutlData.cancel()
  }, [params.id])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (payout && paymentMethod) {
    return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <BreadCrumbs items={
          source === 'transactions'
            ? [
              { item: 'Transactions', to: '/transactions' },
              { item: payout.id, to: `/payout/${payout.id}?source=transactions` }
            ]
            : [
              { item: 'Payouts', to: '/payouts' },
              { item: payout.id, to: '.' }
            ]} />
      </div>
      <Section>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: '32px'
        }}>
          <img src={paymentMethod.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
          <span>{trimZeros(payout.totalPayoutUsd)}</span>
          <span style={{
            background: RADOM_COLORS.GRAY_MED,
            color: RADOM_COLORS.BLACK,
            borderRadius: 10,
            fontSize: 14,
            padding: '5px 10px',
            fontWeight: 400
          }}>{paymentMethod.ticker}</span>
        </div>

        <SectionContent>
          <SectionItem>
            <SubTitle>Network fee</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
              <span>{trimZeros(payout.estimatedFeeUsd)}</span>
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Status</SubTitle>
            <StatusBadge status={camelCase(payout.status) as 'awaitingConfirmation' | 'pending' | 'complete' | 'expired' | 'processing'}>{payout.status}</StatusBadge>
          </SectionItem>

          <SectionItem>
            <SubTitle>Deduct fees</SubTitle>
            <span>{payout.deductFees ? 'Yes' : 'No'}</span>
          </SectionItem>

          <SectionItem>
            <SubTitle>Email receipt</SubTitle>
            <span>{payout.sendEmailReceipts ? 'Yes' : 'No'}</span>
          </SectionItem>

          <SectionItem>
            <SubTitle>
              Created
            </SubTitle>
            <TimeText date={new Date(payout.createdAt)} />
          </SectionItem>

          <SectionItem>
            <SubTitle>
              Completed
            </SubTitle>
            <TimeText date={new Date(payout.completedAt)} />
          </SectionItem>
        </SectionContent>
      </Section>

      {
        managedBalanceLedger &&
              <Section>
                <SubTitle style={{ fontSize: 18 }}>
                      Ledger
                </SubTitle>
                {managedBalanceLedger.map((item, idx) => {
                  const method = getMethod(item.network, item.token)
                  return <SectionContent key={idx}>
                    <SectionItem>
                      <SubTitle>Balance Before</SubTitle>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                        <span>
                          {roundAssetAmount(
                            method.ticker,
                            Number(item.balanceBefore)
                          )}
                        </span>
                        <span style={{ color: RADOM_COLORS.GRAY4 }}>
                          {method.ticker ?? method.logo} on {item.network}
                        </span>
                      </div>
                    </SectionItem>
                    <SectionItem>
                      <SubTitle>Balance After</SubTitle>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                        <span>
                          {roundAssetAmount(
                            method.ticker,
                            Number(item.balanceAfter)
                          )}
                        </span>
                        <span style={{ color: RADOM_COLORS.GRAY4 }}>
                          {method.ticker ?? method.logo} on {item.network}
                        </span>
                      </div>
                    </SectionItem>
                  </SectionContent>
                }
                )}

              </Section>
      }
      <Section>
        <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Withdrawal transactions</SubTitle>
        <Table
          disableHoverStyle
          headers={[
            <span key="address">Payout address</span>,
            <span key="amount">Amount</span>,
            <span key="network">Network</span>,
            <span key="name">Beneficiary Name</span>,
            <span key="email">Beneficiary Email</span>,
            <span key="hash">Transaction Hash</span>
          ]}
          items={payout.payouts.map((item, i) => {
            const customer = customers.find(c => c.id === item.payoutType.crypto?.customerId)
            const hasName = customer?.name && customer.name.length !== 0 && customer.name !== 'null null'
            const hasEmail = customer?.email && customer.email.length
            return ([
              <div key={`${i}-a`} style={{
                display: 'flex',
                gap: 5,
                alignItems: 'center'
              }}>
                <span>{shortAddress(item.payoutType.crypto?.payoutAddress ?? '', 8)}</span>
                <IconButton style={{ padding: 0 }}>
                  <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard
                      .writeText(item.payoutType.crypto?.payoutAddress || '')
                      .then(() => {
                        successToast(
                          'Copied payout address'
                        )
                      })
                      .catch((err) => {
                        console.error('copy failed', err)
                        errorToast(
                          'Failed to copy payout address'
                        )
                      })
                  }} />
                </IconButton>
              </div>,
              <div key={`${i}-b`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
                  <span>{trimZeros(item.payoutType.crypto?.amount || '')}</span>
                </div>
              </div>,
              <div key={`${i}-c`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <img src={paymentMethod.hostChain?.logo ?? paymentMethod.logo} style={{ height: 18, width: 18 }} />
                  <span>
                    { paymentMethod.hostChain?.name ?? paymentMethod.name }
                  </span>
                </div>
              </div>,
              <div key={`${i}-d`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span>
                    {hasName ? customer.name : '-'}
                  </span>
                  {hasName &&
                <IconButton style={{ padding: 0 }}>
                  <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard
                      .writeText(customer?.name ?? '')
                      .then(() => {
                        successToast(
                          'Copied customer name'
                        )
                      })
                      .catch((err) => {
                        console.error('copy failed', err)
                        errorToast(
                          'Failed to copy customer name'
                        )
                      })
                  }} />
                </IconButton>}
                </div>
              </div>,
              <div key={`${i}-e`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span>
                    {hasEmail ? customer.email : '-'}
                  </span>
                  {hasEmail &&
                <IconButton style={{ padding: 0 }}>
                  <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard
                      .writeText(customer?.email ?? '')
                      .then(() => {
                        successToast(
                          'Copied customer email'
                        )
                      })
                      .catch((err) => {
                        console.error('copy failed', err)
                        errorToast(
                          'Failed to copy customer email'
                        )
                      })
                  }} />
                </IconButton>}
                </div>
              </div>,
              <div key={`${i}-f`}>
                <ExternalLinkText
                  style={
                    { textDecoration: 'underline' }
                  }
                  onClick={e => e.stopPropagation()}
                  target="_blank" rel="noreferrer"
                  href={formatExplorerUrl(
                    item.payoutType.crypto?.network || '',
                    'tx',
                    item.payoutEntryReceipt?.crypto?.txHash || ''
                  )}>
                  {shortAddress(item.payoutEntryReceipt?.crypto?.txHash ?? '', 8)}
                  <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
                </ExternalLinkText>
              </div>
            ])
          })}
          isLoading={false} />
      </Section>
    </Container>
  }

  return <div>
    <span>Payout not found</span>
  </div>
})

export default Payout
